import { bookGroups } from "@/utils/bible-books-data";
import type { VerseRef } from "@/utils/verse-reference";

interface VerseGroup {
  createdAt: string;
  tags: string[];
  verses: VerseRef[];
}

interface VerseGroupFilters {
  /** Keep groups containing at least one verse of this book. */
  b?: number;
  /** Keep groups intersecting this canonical category (bookGroups key). */
  cat?: string;
  /** Keep groups carrying this tag. */
  tag?: string;
}

export function compareVerseRefs(a: VerseRef, b: VerseRef): number {
  if (a.book !== b.book) {
    return a.book - b.book;
  }
  if (a.chapter !== b.chapter) {
    return a.chapter - b.chapter;
  }
  return a.verse - b.verse;
}

/**
 * Canonical order by first verse (legacy parity: orderBy "canon"). Groups
 * without linked verses go last; ties fall back to newest first.
 */
export function compareByCanonicalOrder(a: VerseGroup, b: VerseGroup): number {
  const [av] = a.verses;
  const [bv] = b.verses;
  if (!av && !bv) {
    return b.createdAt.localeCompare(a.createdAt);
  }
  if (!av) {
    return 1;
  }
  if (!bv) {
    return -1;
  }
  return compareVerseRefs(av, bv);
}

/** Newest first (legacy parity: orderBy "date"). */
export function compareByDate(a: VerseGroup, b: VerseGroup): number {
  return b.createdAt.localeCompare(a.createdAt);
}

/**
 * Book / canonical-category / tag filters shared by the highlights and
 * notes screens (legacy parity: searchingInBooksFilter + bibleBookCategory).
 */
export function applyVerseGroupFilters<T extends VerseGroup>(
  list: T[],
  filters: VerseGroupFilters,
): T[] {
  let result = list;

  if (filters.b !== undefined) {
    const { b } = filters;
    result = result.filter((g) => g.verses.some((v) => v.book === b));
  }

  if (filters.cat) {
    const group = bookGroups.find((g) => g.key === filters.cat);
    if (group) {
      const idSet = new Set(group.ids);
      result = result.filter((g) => g.verses.some((v) => idSet.has(v.book)));
    }
  }

  if (filters.tag) {
    const { tag } = filters;
    result = result.filter((g) => g.tags.includes(tag));
  }

  return result;
}
